import React from 'react';
import SelectFilter from 'components/DashboardNew/SelectFilter';
import CustomDatePicker from 'components/DashboardNew/CustomDatePicker';
import { clampRangeToBounds, enforceMaxRange, resolveDateInput, resolveRangePreset } from './dateUtils';

const normalizePlacement = (placement) => {
    if (Array.isArray(placement) && placement.length > 0) return placement;
    if (typeof placement === 'string' && placement.trim().length > 0) return [placement];
    return ['header'];
};

const defaultPresets = [];

const SectionFilters = ({
    filtersConfig,
    isFilterable,
    startDate,
    endDate,
    onDateRangeChange,
    onBasicFilterChange,
    variant = 'header',
}) => {
    const config = filtersConfig || {};
    const configPlacements = normalizePlacement(config.placement);
    const allowFilterableUI = config.show !== false;
    const shouldRender = Boolean(
        (isFilterable && allowFilterableUI) || config.show
    );

    if (!shouldRender) return null;

    const list = Array.isArray(config.list) ? config.list : [];
    const nodes = [];
    let dateRangeRendered = false;

    const renderDatePicker = (option = {}, keySuffix = 'default') => {
        if (!onDateRangeChange) return null;
        const dateRangeDefaults = config?.dateRange || {};
        const dateFormat = option.dateFormat || option?.props?.dateFormat || dateRangeDefaults.dateFormat || 'MM/yy';
        const placeholderText = option.placeholderText || option.label || dateRangeDefaults.placeholderText || 'Date Range';
        const showPopperArrow = option.showPopperArrow ?? option?.props?.showPopperArrow ?? dateRangeDefaults.showPopperArrow ?? false;
        const maxRange = option.maxRange ?? dateRangeDefaults.maxRange;

        const minDate = resolveDateInput(option.minDate ?? dateRangeDefaults.minDate);
        const maxDate = resolveDateInput(option.maxDate ?? dateRangeDefaults.maxDate);

        const rawPresets = option.presets ?? dateRangeDefaults.presets ?? defaultPresets;
        const presets = Array.isArray(rawPresets)
            ? rawPresets.map((p, idx) => {
                const label = p?.label ?? p?.title ?? (typeof p === 'string' ? p : `Preset ${idx + 1}`);
                const rangeValue = p?.value ?? p?.range ?? p;
                const range = resolveRangePreset(rangeValue);
                const bounded = clampRangeToBounds(enforceMaxRange(range, maxRange), minDate, maxDate);
                if (!bounded[0] || !bounded[1]) return null;
                return { label, range: bounded };
            }).filter(Boolean)
            : [];

        const handleChange = (nextRange) => {
            const bounded = clampRangeToBounds(enforceMaxRange(nextRange, maxRange), minDate, maxDate);
            onDateRangeChange(bounded);
        };

        return (
            <CustomDatePicker
                key={`filter-date-${keySuffix}`}
                startDate={startDate}
                endDate={endDate}
                onChange={handleChange}
                dateFormat={dateFormat}
                placeholderText={placeholderText}
                showPopperArrow={showPopperArrow}
                minDate={minDate || undefined}
                maxDate={maxDate || undefined}
                presets={presets}
                {...(option.props || {})}
            />
        );
    };

    list.forEach((filterOption, idx) => {
        if (!filterOption) return;
        const optionPlacements = normalizePlacement(filterOption.placement || configPlacements);
        if (!optionPlacements.includes(variant)) return;
        if (filterOption.type === 'basic') {
            nodes.push(
                <SelectFilter
                    key={`filter-basic-${idx}`}
                    label={filterOption.label}
                    options={filterOption.options || []}
                    onChange={(update) => onBasicFilterChange?.(update, filterOption)}
                    defaultValue={filterOption.default}
                    {...(filterOption.props || {})}
                />
            );
            return;
        }

        if (filterOption.type === 'date-range') {
            const picker = renderDatePicker(filterOption, idx);
            if (picker) {
                dateRangeRendered = true;
                nodes.push(picker);
            }
        }
    });

    if (!dateRangeRendered && isFilterable) {
        const dateRangePlacements = normalizePlacement(
            (config.dateRange && config.dateRange.placement) || configPlacements
        );
        if (dateRangePlacements.includes(variant)) {
            const fallbackPicker = renderDatePicker(config.dateRange, 'fallback');
            if (fallbackPicker) {
                nodes.push(fallbackPicker);
            }
        }
    }

    if (nodes.length === 0) return null;

    const containerClasses = [
        'flex items-center gap-2',
        variant === 'meta' ? 'justify-end' : '',
        config.className || ''
    ].filter(Boolean).join(' ');

    return (
        <div className={containerClasses}>
            {nodes}
        </div>
    );
};

export default SectionFilters;


